/** The words the directory uses for owners, stages and dates. Pure, so the list and the drawer say the same thing. */
import type { DirectoryOwners } from './residentDirectoryApi';

export const NO_NAME = 'No name on file';
export const NO_BUILDING = 'No building';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** A stored enum ("HANDED_NOT_RECEIVED") as a phrase ("Handed not received"); words already written for people pass through. */
export function phrase(s: string | null | undefined): string {
  if (!s) return '';
  if (/^[A-Z0-9_]+$/.test(s)) {
    const t = s.toLowerCase().replace(/_/g, ' ');
    return t.charAt(0).toUpperCase() + t.slice(1);
  }
  return s;
}

/** A calendar day ("2026-03-04", or a timestamp that starts with one) as written, with no zone conversion. */
export function fmtDay(s: string | null | undefined): string {
  if (!s) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
  if (!m) return s;
  const mo = Number(m[2]);
  if (mo < 1 || mo > 12) return s;
  return `${MONTHS[mo - 1]} ${Number(m[3])}, ${m[1]}`;
}

/** An instant (a timestamp), in eMerge's clock: New York. */
export function fmtStamp(s: string | null | undefined): string {
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return fmtDay(s);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York', timeZoneName: 'short' });
}

export function money(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '';
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function paOwnerWords(o: DirectoryOwners['pa']): string {
  switch (o.state) {
    case 'HELD': return o.name || 'Held by a PA';
    case 'UNASSIGNED': return 'Nobody (unassigned)';
    case 'CLOSED': return o.name ? `Last case closed (${o.name})` : 'Last case closed';
    default: return 'No PA case';
  }
}

export function financeOwnerWords(o: DirectoryOwners['finance']): string {
  switch (o.state) {
    case 'ACCEPTED': return o.name || 'Accepted by Finance';
    case 'RECEIVED': return 'Received, not yet accepted';
    case 'HANDED_NOT_RECEIVED': return 'Handed, not received';
    default: return 'Not handed to Finance';
  }
}

export const umedOwnerWords = (o: DirectoryOwners): string => o.umed || 'No UMED operator';
export const providerWords = (o: DirectoryOwners): string => (o.providers.length ? o.providers.join(', ') : 'None');
